import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import PropertyCard from "./PropertyCard";
import PropertyFilterPanel from "./PropertyFilterPanel";
import PropertySearchToolbar from "./PropertySearchToolbar";

import {
  fetchProperties,
  togglePropertyLike,
  togglePropertyBookmark,
} from "../api/realty";
import {
  DEFAULT_SORT,
  EMPTY_FILTER,
  buildBackendParams,
  buildPairedSurfaceHref,
  mergeUrlHydratedFilters,
  normalizeSearchSort,
  orderingFromSearchSort,
  propertyCompensationFilterErrorMessage,
  readFiltersFromSearch,
  shouldDeferPropertyFetchUntilSettlementVerified,
  shouldSkipPropertyFetchForSettlement,
  settlementVerificationErrorMessage,
  validatePropertyCompensationFilters,
  writeFiltersToUrl,
} from "../lib/searchFilterContract";

// RealtySearchScreenContent — /search canonical ledger.
//
// Substrate: design/search.html #Csection (.search-section).
// Topology (preserved verbatim):
//
//   .search-section > .container > .row
//     > <PropertyFilterPanel /> (.search-filter aside, offcanvas-driven)
//     > .col.search-results
//         > <PropertySearchToolbar /> (.search-toolbar)
//         > .row#content-section
//             > .card-elem.col-12.col-lg-6.col-xxl-4 (per card)
//                 > <PropertyCard />
//
// Ownership:
//   * URL is canonical truth for the applied filter + sort. Browser
//     back/forward replays the same query.
//   * `filterDraft` is the *uncommitted* form state; "Застосувати"
//     promotes it to URL, which triggers the refetch effect.
//   * `engagement` (liked/bookmarked sets) is route-local. Sets
//     hydrate from `liked_by_me` / `bookmarked_by_me` on the list
//     payload (R3.10B); toggle endpoints return `{liked: bool}` /
//     `{bookmarked: bool}` which we read back into the local sets.
//     No engagement cache, no global sync.
//
// Runtime ownership notes (R2 STEP R2.10):
//   * No optimistic updates. The button click awaits the network
//     response; failure leaves the state unchanged + surfaces the
//     error toast.
//   * No global engagement store.
//
// Backend reconciliation (per backend/app/apps/realty/filters.py
// PropertyFilter):
//   * Mapped: property_type (single), deal_type, currency, city,
//     region, district, min_price, max_price, rooms_min /
//     rooms_max (direct, RIC-4.1A), min_total_area / max_total_area,
//     min_land_area / max_land_area, floor_min, floor_max,
//     land_purpose, suitable_for, residential_complex, cadastral_number,
//     walls, heating, condition, furniture (RIC-4.3),
//     has_recovery_program, has_housing_program, free-text q via DRF
//     SearchFilter (?search=). Sort: newest/-created_at,
//     updated/-updated_at (RIC-4.2), price_asc/price, price_desc/-price.
//   * NOT exposed by choice (backend filter exists, no UI): status
//     (RIC-4.2 — public search is active-only via the viewset gate
//     status=ACTIVE OR created_by=me; lifecycle states belong to the
//     owner's My-Properties surface, not marketplace search), country
//     (RIC-4.2 — Ukraine-focused; defaults to "Україна").
//   * property_type is single-select (RIC-4.1A): exactly one value or
//     none → `?property_type=apartment` or absent. Area routes by it:
//     land → min/max_land_area, otherwise min/max_total_area.
//   * rooms is a direct rooms_min/rooms_max range (RIC-4.1A) — no chip
//     band conversion. The min>max guard is frontend-only, in
//     PropertyFilterPanel (blocks apply).
//   * NOT transported (product-unresolved):
//       - features (multi UI). Classification D — frontend slugs
//         (elevator, ac, …) have no canonical mapping to Feature.id.
//         Inputs disabled in PropertyFilterPanel until taxonomy lands.
//   * Transport: src/api/axios.js paramsSerializer (skips empty values,
//     casts booleans). property_type is now a single scalar param.
//
// Per R2 STEP R2.7: sort options limited to {newest, price_asc,
// price_desc} — popular/trending are backend-absent.

// RIC-5.6C shared filter contract. Marketplace ACTIVE visibility is
// enforced by the backend public catalogue, not a frontend status param.

function buildSearchRequestParams(filters, sort) {
  const base = buildBackendParams(filters);
  return { ...base, ordering: orderingFromSearchSort(sort) };
}

// ---------- payload helpers ---------------------------------------------

const unwrapList = (data) => {
  if (Array.isArray(data)) {
    return { results: data, count: data.length };
  }
  if (data && Array.isArray(data.results)) {
    return { results: data.results, count: data.count ?? data.results.length };
  }
  return { results: [], count: 0 };
};

// ---------- per-card menu construction ----------------------------------
//
// Action set differs by ownership context (R1 Risk R-Q +
// design-map.md §7 — "same primitive, different operational
// weight"). The card itself stays prop-driven; menu derivation lives
// here in the screen content.

function buildPropertyMenu({ property, currentUserId, copiedId, onShare }) {
  const ownerId =
    (property.created_by && property.created_by.id) ||
    (property.created_by && property.created_by.public_id);
  const isOwner =
    currentUserId !== null &&
    currentUserId !== undefined &&
    ownerId !== undefined &&
    String(ownerId) === String(currentUserId);

  const shareItem = {
    id: "share",
    label:
      copiedId === property.id ? "Посилання скопійовано" : "Поширити",
    icon: "fa-solid fa-share-nodes",
    kind: "button",
    // R5.3 — surface-local clipboard copy of /realty/:id. Modal stays
    // open via `dismiss: false` so the label swap remains visible
    // until the user manually closes (backdrop / Escape). No
    // PropertyShare token creation per directive STEP R5.3.3.
    dismiss: false,
    onClick: () => onShare && onShare(property),
  };

  if (isOwner) {
    // My listing — Share + Edit. Lifecycle Sold/Delete live on
    // profile/detail/edit (ACTION-MENUS-UX-B1-01).
    return [
      shareItem,
      {
        id: "edit",
        label: "Редагувати",
        icon: "fa-solid fa-pen-to-square",
        kind: "link",
        // R6.1 — wired to the editRealty surface. Owner-gated at
        // the screen level via R3.9 ownership derivation.
        href: `/realty/${property.id}/edit`,
      },
    ];
  }

  // Viewer list-card menu: Share only. Follow/complaint live on
  // Property detail (ACTION-MENUS-UX-B1-01).
  return [shareItem];
}

// ========================================================================

function RealtySearchScreenContent() {
  const location = useLocation();
  const navigate = useNavigate();

  // Current user — used only to compute per-card menu set.
  // Owner detection is screen-local; PropertyCard remains
  // ownership-agnostic (per R1 + CLAUDE.md Semantic Rendering
  // Doctrine).
  const userInfo = useSelector((state) => state.userLogin && state.userLogin.userInfo);
  const currentUserId =
    (userInfo && (userInfo.id || userInfo.public_id)) || null;

  // ---------- URL-hydrated applied state ----------

  const initial = useMemo(
    () => readFiltersFromSearch(location.search),
    // intentional: hydrate once on mount; subsequent URL writes
    // come from this widget (and trigger the refetch effect via the
    // location dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialSort = useMemo(() => {
    const sort = new URLSearchParams(location.search).get("sort");
    return normalizeSearchSort(sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [appliedFilters, setAppliedFilters] = useState(initial);
  const [sort, setSort] = useState(initialSort);

  // ---------- Uncommitted form draft ----------

  const [filterDraft, setFilterDraft] = useState(initial);

  // ---------- Result state ----------

  const [results, setResults] = useState([]);
  const [count, setCount] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  // ---------- Pagination state (RIC-4.1.1 — "Load More") ----------
  // `hasNext` mirrors the DRF `next` link of the last-fetched page.
  // `isLoadingMore` gates the append request + button affordance.
  // Page accounting lives in refs (not render state): `loadedPageRef` is
  // the highest page currently materialised in `results`; `sigRef` is the
  // backend-param signature of that result set. Both are consulted by the
  // URL effect's no-op guard so a Load-More page bump does not trigger a
  // replace-refetch (which would drop the appended rows + reset scroll).
  const [hasNext, setHasNext] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sigRef = useRef(null);
  const loadedPageRef = useRef(0);

  // ---------- Engagement state (route-local) ----------

  const [likedIds, setLikedIds] = useState(new Set());
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [likingIds, setLikingIds] = useState(new Set());
  const [bookmarkingIds, setBookmarkingIds] = useState(new Set());
  const likingIdsRef = useRef(new Set());
  const bookmarkingIdsRef = useRef(new Set());
  const [engagementError, setEngagementError] = useState(null);

  // R5.3 — Share clipboard-copy state. Per-card "copied" indicator;
  // mirrors the R3.10A copy-link containment philosophy from
  // DetailActionsPanel.js. No timer registry, no cleanup ref — the
  // setTimeout callback uses the functional updater so a setState
  // after unmount is a silent no-op.
  const [copiedId, setCopiedId] = useState(null);
  // R8.3.7 — timer cleanup containment. Mirrors the R8.3.2 ref-based
  // pattern. The functional updater above is still safe across
  // unmount; the ref is defense-in-depth that also clears an
  // in-flight reset when the user spams share on a different card.
  const copiedTimerRef = useRef(null);
  const filterDraftRef = useRef(initial);
  filterDraftRef.current = filterDraft;

  const syncFilterDraft = (updater) => {
    setFilterDraft((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      filterDraftRef.current = next;
      return next;
    });
  };

  // ---------- URL → draft/sort (navigation only) ----------

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilters = readFiltersFromSearch(location.search);
    const sortParam = params.get("sort");
    const nextSort = normalizeSearchSort(sortParam);
    syncFilterDraft((prev) => mergeUrlHydratedFilters(urlFilters, prev));
    setSort(nextSort);
  }, [location.search]);

  // ---------- URL → applied state + accumulating fetch ----------
  //
  // URL is canonical truth for filter + sort + page. This single effect
  // replays the URL on mount, on writeFiltersToUrl, and on browser
  // back/forward. For a given filter signature it materialises pages
  // 1..page (deep-link / refresh / back reproduce the accumulated
  // "Load More" view exactly). Interactive Load-More (handleLoadMore)
  // appends in memory and bumps the URL; the no-op guard below skips the
  // redundant replace-refetch so appended rows + scroll survive.

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilters = readFiltersFromSearch(location.search);
    const nextFilters = mergeUrlHydratedFilters(
      urlFilters,
      filterDraftRef.current,
    );
    const sortParam = params.get("sort");
    const nextSort = normalizeSearchSort(sortParam);
    const urlPage = Math.max(1, parseInt(params.get("page"), 10) || 1);

    setAppliedFilters(nextFilters);

    if (shouldSkipPropertyFetchForSettlement(nextFilters)) {
      setResults([]);
      setCount(null);
      setHasNext(false);
      setLoadState("error");
      setLoadError(settlementVerificationErrorMessage(nextFilters));
      return undefined;
    }

    if (shouldDeferPropertyFetchUntilSettlementVerified(nextFilters)) {
      setLoadState("loading");
      setLoadError(null);
      return undefined;
    }

    // Validate compensation filters before fetch — invalid tokens surface as
    // error state without broadening the query to the backend.
    const compensationValidation = validatePropertyCompensationFilters(nextFilters);
    if (!compensationValidation.valid) {
      setResults([]);
      setCount(null);
      setHasNext(false);
      setLoadState("error");
      setLoadError(
        propertyCompensationFilterErrorMessage(compensationValidation.errors),
      );
      return undefined;
    }

    const sig = JSON.stringify(buildSearchRequestParams(nextFilters, nextSort));

    // No-op guard: the URL write performed by handleLoadMore (a page bump
    // with an unchanged filter signature) is already reflected in
    // `results`. loadedPageRef is advanced BEFORE that navigate, so an
    // exact (sig, page) match means there is nothing new to fetch.
    if (sig === sigRef.current && urlPage === loadedPageRef.current) {
      return undefined;
    }

    sigRef.current = sig;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);
    setIsLoadingMore(false);

    (async () => {
      const base = buildSearchRequestParams(nextFilters, nextSort);
      let acc = [];
      let lastData = null;
      let reached = 0;
      try {
        for (let pg = 1; pg <= urlPage; pg += 1) {
          // eslint-disable-next-line no-await-in-loop
          const data = await fetchProperties({ ...base, page: pg });
          if (cancelled) return;
          lastData = data;
          const { results: rows } = unwrapList(data);
          acc = acc.concat(rows);
          reached = pg;
          if (!data || !data.next) break; // requested page beyond range
        }
        const total = lastData ? unwrapList(lastData).count : 0;
        loadedPageRef.current = reached;
        setResults(acc);
        setCount(total);
        setHasNext(Boolean(lastData && lastData.next));
        // Hydrate engagement Sets from authoritative backend state.
        setLikedIds(new Set(acc.filter((r) => r.liked_by_me).map((r) => r.id)));
        setBookmarkedIds(
          new Set(acc.filter((r) => r.bookmarked_by_me).map((r) => r.id))
        );
        setLoadState("loaded");
      } catch (err) {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    filterDraft.settlement_code_verified,
    filterDraft.settlement_verification_status,
  ]);

  // ---------- Form handlers ----------

  const handleApply = () => {
    writeFiltersToUrl(
      navigate,
      location.pathname,
      filterDraftRef.current,
      sort,
    );
  };

  const handleReset = () => {
    syncFilterDraft(EMPTY_FILTER);
    writeFiltersToUrl(navigate, location.pathname, EMPTY_FILTER, DEFAULT_SORT);
  };

  const handleSortChange = (nextSort) => {
    writeFiltersToUrl(navigate, location.pathname, appliedFilters, nextSort);
  };

  // ---------- Load More (RIC-4.1.1) ----------
  // Fetches the next page and APPENDS it — existing rows are never
  // replaced, so React keeps the mounted cards (keyed by id) and scroll
  // position is preserved. The URL is bumped to ?page=N afterwards so
  // refresh / deep-link / back reproduce the accumulated view; the ref is
  // advanced first so the URL effect recognises the bump as a no-op.

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasNext) return;
    const targetPage = loadedPageRef.current + 1;
    setIsLoadingMore(true);
    setEngagementError(null);
    try {
      const base = buildSearchRequestParams(appliedFilters, sort);
      const data = await fetchProperties({ ...base, page: targetPage });
      const { results: rows } = unwrapList(data);

      setResults((prev) => prev.concat(rows));
      setCount(unwrapList(data).count);
      setHasNext(Boolean(data && data.next));

      // Merge engagement flags for the appended rows only — keep any
      // optimistic toggles already applied to earlier cards.
      setLikedIds((prev) => {
        const nextSet = new Set(prev);
        rows.forEach((r) => {
          if (r.liked_by_me) nextSet.add(r.id);
        });
        return nextSet;
      });
      setBookmarkedIds((prev) => {
        const nextSet = new Set(prev);
        rows.forEach((r) => {
          if (r.bookmarked_by_me) nextSet.add(r.id);
        });
        return nextSet;
      });

      loadedPageRef.current = targetPage;
      const nextParams = new URLSearchParams(location.search);
      nextParams.set("page", String(targetPage));
      navigate(
        { pathname: location.pathname, search: nextParams.toString() },
        { replace: false }
      );
    } catch (err) {
      // Surface inline without destroying the already-loaded grid.
      setEngagementError(messageFromError(err));
    } finally {
      setIsLoadingMore(false);
    }
  };

  // ---------- Engagement handlers ----------

  const handleToggleLike = async (property) => {
    if (!property || likingIdsRef.current.has(property.id)) return;
    likingIdsRef.current.add(property.id);
    setLikingIds(new Set(likingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyLike(property.id);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (data && data.liked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      likingIdsRef.current.delete(property.id);
      setLikingIds(new Set(likingIdsRef.current));
    }
  };

  const handleToggleBookmark = async (property) => {
    if (!property || bookmarkingIdsRef.current.has(property.id)) return;
    bookmarkingIdsRef.current.add(property.id);
    setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyBookmark(property.id);
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (data && data.bookmarked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      bookmarkingIdsRef.current.delete(property.id);
      setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    }
  };

  // R5.3 — Share is a surface-local clipboard side effect. Copies the
  // canonical property URL only; NO PropertyShare token, NO referral,
  // NO analytics, NO cross-surface sync. Failures (Clipboard API
  // unavailable / write rejected) are silent per STEP R5.3.7.
  const handleShareCopy = async (property) => {
    const url = `${window.location.origin}/realty/${property.id}`;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== "function"
      ) {
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopiedId(property.id);
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
      }
      copiedTimerRef.current = window.setTimeout(() => {
        setCopiedId((curr) => (curr === property.id ? null : curr));
      }, 2000);
    } catch (_err) {
      /* silent — clipboard write failed */
    }
  };

  // R8.3.7 — clear pending copiedId reset on unmount.
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  // ---------- JSX ----------

  return (
    <div className="feature-realty-list" data-feature="realty-search">
    <div className="container">
      <div className="row">
        <PropertyFilterPanel
          value={filterDraft}
          onChange={syncFilterDraft}
          onSubmit={handleApply}
          onReset={handleReset}
          offcanvasId="offcanvasFilter"
        />

        <div className="col search-results">
          <PropertySearchToolbar
            count={loadState === "loaded" ? count : null}
            sort={sort}
            onSortChange={handleSortChange}
            mapHref={buildPairedSurfaceHref("/map", appliedFilters, { sort })}
          />

          {engagementError ? (
            <div
              className="feature-realty-list__engagement-error"
              role="alert"
            >
              {engagementError}
            </div>
          ) : null}

          {loadState === "loading" ? (
            <div className="feature-realty-list__state">
              <Spinner size="lg" label="Завантаження оголошень" />
            </div>
          ) : loadState === "error" ? (
            <div className="feature-realty-list__state">
              <EmptyState
                icon="fa-solid fa-triangle-exclamation"
                title="Не вдалося завантажити оголошення"
                description={loadError || "Спробуйте оновити сторінку."}
                size="md"
              />
            </div>
          ) : results.length === 0 ? (
            <div className="feature-realty-list__state">
              <EmptyState
                icon="fa-solid fa-magnifying-glass"
                title="Нічого не знайдено"
                description="Спробуйте змінити або скинути фільтри."
                size="md"
                primaryAction={
                  <button
                    type="button"
                    className="action-btn"
                    onClick={handleReset}
                  >
                    Скинути фільтри
                  </button>
                }
              />
            </div>
          ) : (
            <>
            <div className="row" id="content-section">
              {results.map((property) => {
                const menu = buildPropertyMenu({
                  property,
                  currentUserId,
                  copiedId,
                  onShare: handleShareCopy,
                });
                const detailHref = `/realty/${property.id}`;
                const ownerHandle =
                  property.created_by &&
                  (property.created_by.handle ||
                    property.created_by.public_id);
                const ownerHref = ownerHandle
                  ? `/user/${ownerHandle}`
                  : null;
                return (
                  <div
                    key={property.id}
                    className="card-elem col-12 col-lg-6 col-xxl-4"
                  >
                    <PropertyCard
                      property={property}
                      detailHref={detailHref}
                      ownerHref={ownerHref}
                      liked={likedIds.has(property.id)}
                      bookmarked={bookmarkedIds.has(property.id)}
                      likePending={likingIds.has(property.id)}
                      bookmarkPending={bookmarkingIds.has(property.id)}
                      onToggleLike={() => handleToggleLike(property)}
                      onToggleBookmark={() => handleToggleBookmark(property)}
                      menuItems={menu}
                    />
                  </div>
                );
              })}
            </div>

            {hasNext ? (
              <div className="feature-realty-list__load-more">
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Завантаження…" : "Показати ще"}
                </button>
              </div>
            ) : null}
            </>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

export default RealtySearchScreenContent;
